// src/components/ClientLogos.js
// Reusable client logo grid. Data-driven so adding/removing clients
// only requires adding a line to the CLIENTS (or PARTNERS) array below —
// the layout self-balances and centers the final row, so it always looks
// intentional no matter how many tiles there are.
//
// Source logos come in wildly different aspect ratios and internal padding,
// so height-capping alone leaves some reading smaller than others (circular
// seals in particular waste their bounding box on the ring + corners).
// An optional per-item `scale` multiplier optically enlarges/reins in an
// outlier via a CSS transform — it changes only the rendered artwork, never
// the tile layout, so tuning one logo can't shift the grid.

import React from 'react';

const CLIENTS = [
  {
    name: 'Salem Housing Authority',
    logo: '/salem-logo.png',
    url: 'https://salemha.org',
  },
  {
    name: 'Concord Housing Authority',
    logo: '/concord-logo.png',
    url: 'https://www.concordha.org',
    scale: 1.22, // circular seal — keep in step with the other seals
  },
  {
    name: 'Winchester Housing Authority',
    logo: '/winchester-logo.png',
    url: 'https://www.winchesterha.org',
  },
  {
    name: 'Arlington Housing Authority',
    logo: '/arlington-logo.png',
    url: 'https://arlingtonhousing.org',
  },
  {
    name: 'Waltham Housing Authority',
    logo: '/waltham-logo.png',
    url: 'https://walhouse.org',
    scale: 1.22, // circular seal — enlarge to match edge-filling logos
  },
  {
    name: 'Marblehead Housing Authority',
    logo: '/marblehead-logo.svg',
    url: 'https://www.marbleheadha.org',
  },
  {
    name: 'Pittsfield Housing Authority',
    logo: '/pittsfield-logo.webp',
    url: 'https://pittsfieldhousing.org',
  },
  {
    name: 'Foxborough Housing Authority',
    logo: '/foxboro-logo.png',
    url: 'https://www.foxborohousing.com',
    scale: 1.33, // circular seal — thicker ring, so needs a bit more to match the others optically
  },
  {
    name: 'Somerville Housing Authority',
    logo: '/somerville-logo.jpg',
    url: 'https://sha-web.org',
  },
  {
    name: 'Whitman Housing Authority',
    logo: '/whitman-logo.png',
    url: 'https://www.whitmanhousing.com',
  },
  {
    name: 'Andover Housing Authority',
    logo: '/andover-logo.png',
    url: 'http://andoverhousing.org',
    scale: 1.18, // baked-in white margins make the artwork read small — nudge up
  },
  {
    name: 'Bunkie Housing Authority',
    logo: '/bunkie-logo.png',
    url: 'https://hacbla.org',
  },
  {
    name: 'Brick Housing Authority',
    logo: '/brick-logo.png',
    url: 'https://www.brickha.org',
  },
  {
    name: 'Hadley Housing Authority',
    logo: '/hadley-logo.png',
    url: 'https://www.hadleyhousingauthority.com',
  },
  {
    name: 'Sarasota Housing Authority',
    logo: '/sarasota-logo.png',
    url: 'https://sarasotahousing.org',
  },
  {
    name: 'Quincy Housing Authority',
    logo: '/quincy-logo.png',
    url: 'https://quincyha.com',
    scale: 1.22, // circular seal
  },
  {
    name: 'Hingham Housing Authority',
    logo: '/hingham-logo.png',
    url: 'https://hinghamha.com',
    scale: 1.22, // circular seal
  },
  {
    name: 'Marshfield Housing Authority',
    logo: '/marshfield-logo.png',
    url: 'https://marshfieldha.com',
    scale: 1.22, // circular seal
  },
  {
    name: 'Holbrook Housing Authority',
    logo: '/holbrook-logo.png',
    url: 'https://holbrookha.com',
    scale: 1.22, // circular seal
  },
  {
    name: 'Hanson Housing Authority',
    logo: '/hanson-logo.png',
    url: 'https://hansonha.com',
    scale: 1.22, // circular seal
  },
];

const PARTNERS = [
  {
    name: 'PHAnetwork',
    logo: '/phanetwork-logo.jpg',
    url: 'https://www.phanetwork.net/',
    badge: 'Partner',
  },
  {
    name: 'PHA-Web',
    logo: '/phaweb-logo.svg',
    url: 'https://www.pha-web.com',
    badge: 'Partner',
  },
];

/**
 * ClientLogos
 * @param {'compact'|'default'} size       compact = smaller tiles (for dense homepage strip)
 * @param {boolean}             showNames  whether to show the authority name under its logo
 *
 * Layout note: uses a centered flex-wrap rather than a fixed-column grid so the
 * final (partial) row centers itself. Adding clients never requires touching layout.
 */
export default function ClientLogos({ size = 'default', showNames = true }) {
  const items = [...CLIENTS, ...PARTNERS];

  const tileHeight = size === 'compact' ? 'h-14 md:h-16' : 'h-16 md:h-20';
  const logoCap = size === 'compact' ? 'max-h-10 md:max-h-12' : 'max-h-12 md:max-h-16';

  // Responsive fixed widths so tiles flow & wrap, last row auto-centers.
  // 2-up on mobile, 3-up on small, 6-up on large — matches the previous grid density.
  const tileBasis =
    'basis-[calc(50%-0.5rem)] sm:basis-[calc(33.333%-0.667rem)] lg:basis-[calc(16.666%-0.834rem)]';

  return (
    <div className="flex flex-wrap justify-center gap-3 md:gap-4">
      {items.map((item) => (
        <a
          key={item.name}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          title={item.name}
          className={`${tileBasis} group relative bg-white rounded-2xl p-4 shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 flex flex-col items-center justify-center`}
        >
          <div className={`${tileHeight} w-full flex items-center justify-center`}>
            <img
              src={item.logo}
              alt={item.name}
              loading="lazy"
              className={`${logoCap} max-w-full object-contain`}
              style={item.scale ? { transform: `scale(${item.scale})` } : undefined}
            />
          </div>
          {showNames && (
            <p className="mt-2 text-[11px] md:text-xs font-medium text-gray-600 text-center leading-tight">
              {item.name}
            </p>
          )}
          {item.badge && (
            <span className="absolute top-2 right-2 text-[9px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
              {item.badge}
            </span>
          )}
        </a>
      ))}
    </div>
  );
}